/**
 * /car-paint-code — «how much paint to buy» and «how to mix it», without a
 * page (the page is calc-ui in index.js). Tested in node:
 * tests/js/car-paint-calc.test.mjs.
 *
 * Two questions people bring after the code (Wordstat, September 2026:
 * «сколько краски нужно на покраску автомобиля», «расход краски на деталь»,
 * «пропорции смешивания краски с отвердителем»):
 *   - how many cans / how much ready-to-spray paint for these parts;
 *   - how much hardener and thinner goes with the paint in the cup.
 * Nothing here knows a brand: the ratio comes off the reader's own can, the
 * coverage defaults come from makers' data sheets (named where they are used).
 */

/* ── mixing ratios ───────────────────────────────────────────────────────── */

/**
 * What is printed on a can, as people type it:
 *   «2:1», «4:1», «2:1:1», «100:50:10», «2:1 +10%», «2:1+10-15%», «3 к 1»,
 *   «1:1 (50%)» — a range takes its middle, a comma is a decimal point.
 * → { parts: [n…] (2–4 positive numbers), pct: n|null } or null.
 * `pct` is thinner «as a percentage» — of what, the caller decides (see mix()).
 */
export function parseRatio(s) {
    let t = String(s == null ? '' : s).toLowerCase().replace(/,/g, '.').replace(/\s+/g, ' ').trim();
    if (!t) return null;
    let pct = null;
    const pm = /\+?\s*(\d+(?:\.\d+)?)(?:\s*[-–]\s*(\d+(?:\.\d+)?))?\s*%/.exec(t);
    if (pm) {
        pct = pm[2] != null ? (parseFloat(pm[1]) + parseFloat(pm[2])) / 2 : parseFloat(pm[1]);
        t = (t.slice(0, pm.index) + ' ' + t.slice(pm.index + pm[0].length)).trim();
    }
    // «3 к 1», «3 to 1», «3 zu 1», «3 a 1», «3 pour 1» — a word between two numbers is a colon
    t = t.replace(/(\d)\s*(?:к|to|zu|a|à|pour|para|banding|대)\s*(\d)/g, '$1:$2').replace(/[\/x×]/g, ':');
    const nums = t.split(':').map((x) => x.replace(/[^\d.]/g, '')).filter((x) => x !== '');
    if (nums.length < 2 || nums.length > 4) return null;
    const parts = nums.map(Number);
    if (parts.some((n) => !isFinite(n) || n <= 0)) return null;
    if (pct != null && (!isFinite(pct) || pct < 0 || pct > 200)) return null;
    return { parts, pct };
}

/**
 * Volumes of each component for a total `total` of ready-to-spray mix (or,
 * with `from: 'first'`, when you have `total` of the FIRST component — the
 * paint — and want the rest). `pctOf`: 'mix' — the percentage is of paint +
 * hardener, 'paint' — of the paint alone. Data sheets write both ways and
 * rarely say which, so the page asks instead of guessing.
 * → { amounts: [paint, hardener, …, thinner?], total }
 */
export function mix(ratio, total, opts) {
    const o = opts || {};
    const r = typeof ratio === 'string' ? parseRatio(ratio) : ratio;
    if (!r || !(total > 0)) return null;
    const parts = r.parts.slice();
    const sumParts = parts.reduce((a, b) => a + b, 0);
    const pct = r.pct || 0;
    // everything in «parts»: thinner as a percentage becomes one more part
    let thinner = 0;
    if (pct) thinner = o.pctOf === 'paint' ? parts[0] * pct / 100 : sumParts * pct / 100;
    const all = pct ? parts.concat([thinner]) : parts;
    const unit = o.from === 'first' ? total / parts[0] : total / all.reduce((a, b) => a + b, 0);
    const amounts = all.map((p) => p * unit);
    return { amounts, total: amounts.reduce((a, b) => a + b, 0), thinner: !!pct };
}

/** Marks on a mixing cup: fill the paint to the first line, the hardener up
    to the second, and so on — cumulative, as painters pour. */
export function cupMarks(amounts) {
    let acc = 0;
    return amounts.map((a) => (acc += a));
}

/** Sensible rounding of a volume: under 100 ml to the millilitre (thinner
    and hardener for a small mix — 27 ml is not 25), to 5 ml under a litre,
    to 10 ml above. Formatting is the page's job. */
export function roundMl(ml) {
    if (!(ml > 0)) return 0;
    if (ml < 100) return Math.round(ml);
    if (ml < 1000) return Math.round(ml / 5) * 5;
    return Math.round(ml / 10) * 10;
}

/* ── how much paint ──────────────────────────────────────────────────────── */

/**
 * Ready-to-spray volume (litres) to lay `dft` µm of dry film over `area` m²:
 *   L = area · dft / (10 · vs · te)
 * vs — volume solids of the READY mix, %, from the data sheet;
 * te — transfer efficiency of the gun (share of paint that lands);
 * the 10 turns µm·m² into litres at 100 % solids (1 µm over 1 m² = 1 ml).
 */
export function sprayLitres(area, dft, vs, te) {
    if (!(area > 0) || !(dft > 0) || !(vs > 0) || !(te > 0)) return 0;
    return area * dft / (10 * vs * te);
}

/**
 * Cans for `area` m² when one can covers `perCan` m² with one coat,
 * `coats` coats: rounded UP (a partial can is a can to buy).
 * → { cans, exact }
 */
export function cansFor(area, coats, perCan) {
    if (!(area > 0) || !(coats > 0) || !(perCan > 0)) return { cans: 0, exact: 0 };
    const exact = area * coats / perCan;
    return { cans: Math.max(1, Math.ceil(exact - 1e-9)), exact };
}

/* ── the paint system a finish needs ─────────────────────────────────────── */

/** Which layers a colour needs, by its finish: a solid colour is one 2K
    enamel coat system (or base + clear, as many modern cars are painted);
    metallic / pearl / mica are base + clear; a tri-coat has a mid-coat. */
export function layersFor(finish) {
    if (finish === 'tricoat') return ['base', 'mid', 'clear'];
    if (finish === 'metallic' || finish === 'pearl' || finish === 'mica') return ['base', 'clear'];
    return ['enamel'];
}
